/**
 * PROTOCOL panel — CRE
 * 1) Upload referral file → fileId
 * 2) Analyze (V2 default for structured table + selected card)
 */
import React, { useState, useCallback, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../Hooks/useAuth";
import { uploadReferralFiles, analyzeReferral } from "../../api/protocolApi";
import {
  resolveCreFeatureNotAllowedMessage,
} from "../../cre/crePermissions";
import { toast } from "react-toastify";
import { FiLoader, FiUpload } from "react-icons/fi";
import { FaListAlt } from "react-icons/fa";
import {
  MODEL_V1,
  MODEL_V2,
  MODEL_V3,
  getDisplayExtractedText,
  getEffectiveSelectedProtocol,
  getV2TableColumnKeys,
  hasAutoSelectedProtocol,
  isV3Model,
} from "./protocolPanelUtils";
import {
  ProtocolAnatomyRow,
  ProtocolMatchStatus,
  ProtocolUnableToDetermineAlert,
  RelevantProtocolsSection,
  SelectedProtocolSection,
} from "./ProtocolPanelViews";

const ACCEPT = ".jpg,.jpeg,.png,.gif,.bmp,.pdf";
const MAX_MB = 30;

export default function ProtocolPanel({ language }) {
  const { t } = useTranslation("protocol");
  const accessToken = useAuth();
  const fileInputRef = useRef(null);

  const [uploadedFileId, setUploadedFileId] = useState(null);
  const [uploadedFileCount, setUploadedFileCount] = useState(0);
  const [uploadedFileName, setUploadedFileName] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [model, setModel] = useState(MODEL_V2);
  /** Model used for the current `result` — UI layout follows this, not the dropdown alone. */
  const [analysisModel, setAnalysisModel] = useState(null);
  const [manualSelectedProtocol, setManualSelectedProtocol] = useState(null);

  const handleFileSelect = useCallback(
    async (fileList) => {
      const files = fileList ? Array.from(fileList) : [];
      if (!files.length || !accessToken) return;
      const overSize = files.find((f) => f.size > MAX_MB * 1024 * 1024);
      if (overSize) {
        toast.error(t("toastFileTooBig", { max: MAX_MB }));
        return;
      }
      setUploading(true);
      setError(null);
      setResult(null);
      setAnalysisModel(null);
      setManualSelectedProtocol(null);
      setUploadedFileId(null);
      setUploadedFileName(null);
      setUploadedFileCount(0);
      try {
        const payload = await uploadReferralFiles(files, accessToken);
        setUploadedFileId(payload.fileId);
        setUploadedFileCount(files.length);
        setUploadedFileName(
          files.length === 1
            ? payload.originalFileName || files[0].name
            : payload.originalFileName || `${files.length} ${t("filesCombined")}`
        );
      } catch (err) {
        const msg =
          resolveCreFeatureNotAllowedMessage(err) ||
          err.message ||
          t("toastUploadFailed");
        setError(msg);
        toast.error(msg);
      } finally {
        setUploading(false);
      }
    },
    [accessToken, t]
  );

  const onInputChange = useCallback(
    (e) => {
      const fileList = e.target.files;
      if (fileList?.length) handleFileSelect(fileList);
      e.target.value = "";
    },
    [handleFileSelect]
  );

  const runAnalysis = useCallback(async () => {
    if (!uploadedFileId || !accessToken) return;
    setLoadingAnalysis(true);
    setError(null);
    setResult(null);
    setAnalysisModel(null);
    setManualSelectedProtocol(null);
    const modelForRun = model;
    try {
      const payload = await analyzeReferral(
        { fileId: uploadedFileId, model: modelForRun, language },
        accessToken
      );
      setResult(payload);
      setAnalysisModel(modelForRun);
      if (payload.detectedAnatomy === "unknown") {
        toast.warning(t("toastAnatomyUnknown"));
      }
    } catch (err) {
      const msg =
        resolveCreFeatureNotAllowedMessage(err) ||
        err.message ||
        t("toastAnalysisFailed");
      setError(msg);
      toast.error(msg);
    } finally {
      setLoadingAnalysis(false);
    }
  }, [accessToken, uploadedFileId, model, language, t]);

  const clearFile = useCallback(() => {
    setUploadedFileId(null);
    setUploadedFileCount(0);
    setUploadedFileName(null);
    setResult(null);
    setAnalysisModel(null);
    setError(null);
    setManualSelectedProtocol(null);
  }, []);

  const modelChangedSinceAnalysis =
    Boolean(result && analysisModel && model !== analysisModel);

  const copyToClipboard = useCallback(
    async (text, labelKey) => {
      if (!text) return;
      try {
        await navigator.clipboard.writeText(text);
        toast.success(t("toastCopied", { label: labelKey ? t(labelKey) : "" }));
      } catch {
        toast.error(t("toastCopyFailed"));
      }
    },
    [t]
  );

  const effectiveSelected = useMemo(
    () => getEffectiveSelectedProtocol(result, manualSelectedProtocol),
    [result, manualSelectedProtocol]
  );

  const protocolMatched = Boolean(
    effectiveSelected || hasAutoSelectedProtocol(result)
  );

  const showUnableAlert =
    result &&
    !protocolMatched &&
    (result.recommendedProtocols?.length ?? 0) > 0;

  const copyTable = useCallback(() => {
    if (!result?.recommendedProtocols?.length) return;
    const columnKeys = getV2TableColumnKeys(result.recommendedProtocols);
    const header = [t("colProtocol"), ...columnKeys.map((k) => k)].join("\t");
    const rows = result.recommendedProtocols.map((row) => {
      const af = row.additionalFields || {};
      return [
        row.protocolName,
        ...columnKeys.map((k) => (af[k] != null ? String(af[k]) : "")),
      ].join("\t");
    });
    copyToClipboard([header, ...rows].join("\n"), "relevantProtocolsLabel");
  }, [result, t, copyToClipboard]);

  const unknownAnatomy = result?.detectedAnatomy === "unknown";

  return (
    <div className="mt-6 dark:bg-gray-900">
      <div className="flex flex-col">
        <div className="p-4 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-3xl overflow-hidden shadow-sm dark:shadow-gray-900">
          <div className="flex items-center gap-2 mb-4">
            <FaListAlt className="text-[#A4C639] dark:text-[#CBEA7B]" size={20} />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {t("title")}
            </h2>
          </div>

          {!uploadedFileId && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t("uploadLabel", { max: MAX_MB })}
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT}
                multiple
                onChange={onInputChange}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || !accessToken}
                className="w-full flex items-center justify-center gap-2 py-4 px-4 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-[#A4C639] dark:hover:border-[#CBEA7B] bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300 disabled:opacity-50"
              >
                {uploading ? (
                  <>
                    <FiLoader className="animate-spin" />
                    {t("uploading")}
                  </>
                ) : (
                  <>
                    <FiUpload />
                    {t("chooseFile")} {t("chooseFilesMultiple")}
                  </>
                )}
              </button>
            </div>
          )}

          {uploadedFileId && (
            <>
              <div className="mb-4 flex items-center justify-between gap-2 flex-wrap">
                <span className="text-sm text-gray-600 dark:text-gray-400 truncate">
                  {uploadedFileCount > 1 ? `${uploadedFileCount} files → ` : ""}
                  {uploadedFileName}
                </span>
                <button
                  type="button"
                  onClick={clearFile}
                  className="text-xs text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                >
                  {t("removeFile")}
                </button>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t("model")}
                </label>
                <select
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  disabled={loadingAnalysis}
                  className="w-full max-w-md rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
                >
                  <option value={MODEL_V1}>{t("modelV1")}</option>
                  <option value={MODEL_V2}>{t("modelV2")}</option>
                  <option value={MODEL_V3}>{t("modelV3")}</option>
                </select>
              </div>
              <button
                type="button"
                onClick={runAnalysis}
                disabled={loadingAnalysis}
                className="mb-6 px-4 py-2 rounded-lg xl:rounded-full bg-[#A4C639] dark:bg-[#CBEA7B] text-gray-900 dark:text-black font-medium hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
              >
                {loadingAnalysis ? (
                  <>
                    <FiLoader className="animate-spin" />
                    {t("analyzing")}
                  </>
                ) : (
                  t("runAnalysis")
                )}
              </button>
            </>
          )}

          {error && (
            <div className="mb-4 p-3 rounded bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 text-sm">
              {error}
            </div>
          )}

          {result && (
            <div className="space-y-4">
              {modelChangedSinceAnalysis && (
                <p className="text-sm text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-900/25 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2">
                  {t("modelChangedHint")}
                </p>
              )}
              <ProtocolAnatomyRow
                detectedAnatomy={result.detectedAnatomy}
                unknownAnatomy={unknownAnatomy}
                onCopy={copyToClipboard}
                t={t}
              />

              <ProtocolMatchStatus protocolMatched={protocolMatched} t={t} />

              {showUnableAlert && <ProtocolUnableToDetermineAlert t={t} />}

              <RelevantProtocolsSection
                protocols={result.recommendedProtocols}
                selectedProtocol={effectiveSelected}
                onSelectRow={setManualSelectedProtocol}
                onCopyTable={copyTable}
                t={t}
              />

              <SelectedProtocolSection
                model={analysisModel}
                protocol={effectiveSelected}
                explanation={result.explanation}
                extractedText={result.extractedText}
                getDisplayExtractedText={getDisplayExtractedText}
                language={language}
                onCopy={copyToClipboard}
                t={t}
              />

              {!isV3Model(analysisModel) && result.extractedText && (
                  <div>
                    <details className="text-sm">
                      <summary className="cursor-pointer text-blue-600 dark:text-blue-400 font-medium">
                        {t("showExtractedText")}
                      </summary>
                      <textarea
                        readOnly
                        value={getDisplayExtractedText(result.extractedText)}
                        className="mt-2 w-full min-h-24 h-40 rounded border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 p-2 text-xs font-mono"
                      />
                    </details>
                  </div>
                )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
